import {
  Box,
  Card,
  CardContent,
  Checkbox, FormControl, FormControlLabel, FormGroup, Grid, MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import CloseButton from '../Buttons/Close';
import BlockData from '../../models/BlockData';
import BlockType from '../../models/BlockType';
import { URLOpenType } from '../../models/URLOpenType';
import { URLTargetType } from '../../models/URLTargetType';
import LinkData from '../../models/LinkData';
import ErrorText from '../ErrorText';
import ButtonSizePicker from './ButtonSettingsModal/components/ButtonSizePicker';
import ButtonColorPicker from './ButtonSettingsModal/components/ButtonColorPicker';
import { ColorPickerColors } from '../../models/ColorPicker';
import { ButtonComponentSettings, ButtonSize } from '../../models/BlockComponentValues/BlockComponentButton';
import { PagesContext } from '../../contexts/PagesContext';
import { FileContext, FileInterface } from '../../contexts/FileContext';
import EditableText from '../Form/EditableText';

interface Props {
  openModal: boolean;
  link: LinkData;
  blockData: BlockData[];
  initialTextColor?: string;
  hideRemoveButton?: boolean;
  errorText?: string;
  onRemoveBlock?: () => void;
  onChangeURLTitle?: (title: string) => void;
  onChangeLink?: (link: string) => void;
  onChangeFile: (file: FileInterface) => void;
  onSelectBlock?: (selected: string) => void;
  onChangeTargetType?: (target: any) => void;
  onChangeOpenType?: (checked: boolean) => void;
  settings: ButtonComponentSettings;
  onChangeSize?: (target: ButtonSize) => void;
  onChangeColor?: (target: ColorPickerColors) => void;
  onChangeBorderColor?: (target: ColorPickerColors) => void;
  onCloseModal: (event: React.MouseEvent) => void;
  onSaveModal: (event: React.MouseEvent) => void;
}

function LinkModal({
  openModal,
  link,
  initialTextColor,
  blockData,
  hideRemoveButton = false,
  errorText,
  onRemoveBlock,
  onChangeURLTitle,
  onChangeLink,
  onChangeFile,
  onSelectBlock,
  onChangeTargetType,
  onChangeOpenType,
  settings,
  onChangeSize,
  onChangeColor,
  onChangeBorderColor,
  onCloseModal,
  onSaveModal,
}: Props) {
  const [externalUrl, setExternalUrl] = useState(link.linkData.url);
  const { getPageSubpages } = useContext(PagesContext);
  const { files, fetch } = useContext(FileContext);

  const urlOptions = getPageSubpages().map((subpage) => (
    <MenuItem value={`/${subpage.url_path}`} key={subpage.id}>
      {`https://${subpage.custom_url}/${subpage.url_path} (${subpage.subpage_title})`}
    </MenuItem>
  ));

  const blockOptions = blockData.map((block, index) => (
    <MenuItem value={`#${block.id}`} key={block.id}>{`第${index}ブロック：${BlockType.describe(block.type)} (${block.id})`}</MenuItem>
  ));

  const matchBlockId = useCallback(
    (url?: string) => blockData.some(((item) => `#${item.id}` === url)),
    [blockData],
  );

  const getMatchingSubpage = (url: string) => {
    const matchingSubpage = getPageSubpages().find((subpage) => url === `/${subpage.url_path}`);
    return matchingSubpage ? `/${matchingSubpage.url_path}` : '';
  };

  const getMatchingFile = (filePath: string) => {
    if (filePath.includes('files')) {
      const fileName = filePath.split('/').pop();
      const file = files.find((f) => f.file_name === fileName);
      if (!file) return '';
      return file.id;
    }

    const file = files.find((f) => f.path === filePath);
    if (!file) return '';
    return file.id;
  };

  const display = useMemo(() => (openModal ? 'block' : 'none'), [openModal]);

  useEffect(() => {
    if (urlOptions.length === 0 && link.linkData.targetType === URLTargetType.CUSTOM_URL) {
      onChangeLink('');
    }
  }, [urlOptions, onChangeLink, link.linkData.targetType]);

  useEffect(() => {
    if (link.linkData.targetType === URLTargetType.CUSTOM_URL) {
      onChangeTargetType(URLTargetType.EXTERNAL);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <>
      <div style={{ display }} className="modal" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content border-0 fullViewportHeight">
            <div className="modal-header">
              <h5 className="modal-title">ボタン設定</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={onCloseModal} />
            </div>
            <div className="modal-body">
              <Card>
                <CardContent>
                  <Grid container mb={2} spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" className="text-bold" mb={2} color="#495057">
                        表示テキスト
                      </Typography>
                      {!hideRemoveButton && <CloseButton updateHandler={onRemoveBlock} />}
                      <div className="editable-text-container">
                        <EditableText
                          text={link.title}
                          textColor={initialTextColor}
                          isEditable
                          updateHandler={(value) => onChangeURLTitle(value)}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <hr className="mt-4" />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" className="text-bold" mb={2} color="#495057">
                        リンク先の設定
                      </Typography>
                      <Box mb={2}>
                        <ToggleButtonGroup
                          color="primary"
                          value={link.linkData.targetType}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue) {
                              if (newValue === URLTargetType.EXTERNAL) {
                                onChangeLink('');
                                setExternalUrl('');
                              }
                              onChangeTargetType(newValue);
                            }
                          }}
                          size="small"
                        >
                          <ToggleButton value={URLTargetType.INTERNAL}>ページ内リンク</ToggleButton>
                          <ToggleButton value={URLTargetType.EXTERNAL}>直接URLを入力</ToggleButton>
                          <ToggleButton value={URLTargetType.FILE}>ファイルリンク</ToggleButton>
                          {urlOptions.length > 0 && (<ToggleButton value={URLTargetType.CUSTOM_URL}>サイト内リンク</ToggleButton>)}
                          {(link.linkData.targetType === URLTargetType.EXTERNAL || link.linkData.targetType === URLTargetType.CUSTOM_URL) && (
                            <FormGroup sx={{ marginLeft: '10px' }}>
                              <FormControlLabel
                                control={(
                                  <Checkbox
                                    size="small"
                                    checked={link.linkData.openType === URLOpenType.NEW_TAB}
                                    onChange={(event) => onChangeOpenType(event.target.checked)}
                                  />
                                )}
                                label="別タブで開く"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                              />
                            </FormGroup>
                          )}
                        </ToggleButtonGroup>
                      </Box>

                      {link.linkData.targetType === URLTargetType.INTERNAL && (
                        <Box>
                          <FormControl size="small">
                            <Select
                              value={matchBlockId(link.linkData.url) ? link.linkData.url : ''}
                              onChange={(event) => onSelectBlock(event.target.value)}
                              displayEmpty
                            >
                              <MenuItem value="" key="">
                                <em>選択してください</em>
                              </MenuItem>
                              {blockOptions}
                            </Select>
                          </FormControl>
                        </Box>
                      )}

                      {link.linkData.targetType === URLTargetType.EXTERNAL && (
                        <Box>
                          <TextField
                            required
                            fullWidth
                            label="リンク"
                            defaultValue={externalUrl}
                            onChange={(event) => onChangeLink(event.target.value)}
                            size="small"
                          />
                        </Box>
                      )}

                      {link.linkData.targetType === URLTargetType.CUSTOM_URL && (
                        <Box>
                          <FormControl size="small">
                            <Select
                              value={getMatchingSubpage(link.linkData.url)}
                              onChange={(event) => onChangeLink(event.target.value)}
                              displayEmpty
                            >
                              <MenuItem value="" key="">
                                <em>選択してください</em>
                              </MenuItem>
                              {urlOptions}
                            </Select>
                          </FormControl>
                        </Box>
                      )}

                      {link.linkData.targetType === URLTargetType.FILE
                        && (
                          <>
                            <FormControl size="small" fullWidth>
                              {files.length === 0 ? (
                                <div className="m-1">
                                  ファイルがアップロードされていません
                                </div>
                              ) : (
                                <Select
                                  value={getMatchingFile(link.linkData.url)}
                                  onChange={(event) => onChangeFile(files.find((file) => file.id === Number(event.target.value)))}
                                  displayEmpty
                                >
                                  <MenuItem value="" key="">
                                    <em>選択してください</em>
                                  </MenuItem>
                                  {files.map((file) => (
                                    <MenuItem value={file.id} key={file.id}>
                                      {file.file_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            </FormControl>
                          </>
                        )}

                    </Grid>
                  </Grid>
                  <hr className="mt-4" />
                  <Grid
                    container
                    mt={2}
                    direction="column"
                    alignItems="start"
                    justifyContent="center"
                  >
                    <Typography variant="subtitle2" className="text-bold" mb={2} color="#495057">
                        カラー設定
                    </Typography>
                    <Grid item mb={2}>
                      <ButtonColorPicker value={settings.color} onChangeColor={(value) => onChangeColor(value as ColorPickerColors)} />
                    </Grid>
                    <Grid item>
                      <ButtonColorPicker value={settings.border} colorPickerLabel="枠線の色" onChangeColor={(borderColor) => onChangeBorderColor(borderColor as ColorPickerColors)} />
                    </Grid>
                  </Grid>
                  <hr className="mt-4" />
                  <Grid container spacing={2}>
                    <Grid item>
                      <Typography variant="subtitle2" className="text-bold" mb={2} color="#495057">
                        サイズ設定
                      </Typography>
                      <ButtonSizePicker value={settings.size} onChangeSize={(value) => onChangeSize(value)} />
                    </Grid>
                  </Grid>
                  {/* <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    mt={2}
                  >
                    <Button variant="outlined" color="primary" onClick={onCloseModal}>
                      キャンセル
                    </Button>

                    {errorText ? <ErrorText text={errorText} /> : null}

                    <Button variant="contained" color="primary" onClick={onSaveModal}>
                      適用
                    </Button>
                  </Stack> */}
                </CardContent>
              </Card>
            </div>
            <div className="modal-footer">
              {errorText ? <ErrorText text={errorText} /> : null}
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={onCloseModal}>キャンセル</button>
              <button type="button" className="btn btn-primary" onClick={onSaveModal}>変更</button>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display }} className="modal-backdrop fade show" />
    </>
  );
}

export default LinkModal;
